<template>
  <div class="occasion-step">
    <div class="occasion-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Form Fields -->
      <div class="form-fields">
        <!-- Occasion Radio Buttons -->
        <div class="field-group">
          <label class="field-label">Случай</label>
          <div class="radio-group">
            <div
              v-for="occasion in step.occasions"
              :key="occasion.value"
              class="radio-item"
            >
              <div class="radio-option">
                <RadioButton
                  :inputId="`occasion-${occasion.value}`"
                  v-model="selectedOccasion"
                  :value="occasion.value"
                  @update:model-value="updateOccasion"
                />
                <label :for="`occasion-${occasion.value}`" class="radio-label">
                  {{ occasion.label }}
                </label>
              </div>
              
              <!-- Additional Select for occasions with subOptions -->
              <div
                v-if="selectedOccasion === occasion.value && occasion.subOptions && occasion.subOptions.length > 0"
                class="sub-option-select"
              >
                <Select
                  v-model="selectedSubOptions[occasion.value]"
                  :options="occasion.subOptions"
                  optionLabel="label"
                  optionValue="value"
                  :placeholder="`Выберите ${occasion.label.toLowerCase()}`"
                  class="field-select"
                  @update:model-value="(value: string | null) => updateSubOption(occasion.value, value)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import Select from 'primevue/select'
import RadioButton from 'primevue/radiobutton'
import { useGiftModal } from '@/features/gift/store'
import type { GiftStep } from '@/features/gift/types/gift-config'

interface Props {
  step: GiftStep & { type: 'occasion' }
}

const props = defineProps<Props>()
const giftStore = useGiftModal()

const selectedOccasion = ref<string | null>(null)
// Храним выбранные дополнительные опции для каждого случая: { occasionValue: subOptionValue }
const selectedSubOptions = ref<Record<string, string | null>>({})

function updateOccasion() {
  giftStore.updateGiftData(props.step.id, selectedOccasion.value)
  
  // Очищаем subOptions для случая, который больше не выбран
  if (selectedOccasion.value) {
    // Очищаем все subOptions кроме текущего выбранного случая
    Object.keys(selectedSubOptions.value).forEach(occasionValue => {
      if (occasionValue !== selectedOccasion.value) {
        selectedSubOptions.value[occasionValue] = null
        giftStore.updateGiftData(`${props.step.id}_${occasionValue}`, null)
        delete selectedSubOptions.value[occasionValue]
      }
    })
  } else {
    // Если ничего не выбрано, очищаем все subOptions
    Object.keys(selectedSubOptions.value).forEach(occasionValue => {
      selectedSubOptions.value[occasionValue] = null
      giftStore.updateGiftData(`${props.step.id}_${occasionValue}`, null)
    })
    selectedSubOptions.value = {}
  }
}

function updateSubOption(occasionValue: string, value: string | null) {
  selectedSubOptions.value[occasionValue] = value
  giftStore.updateGiftData(`${props.step.id}_${occasionValue}`, value)
}

onMounted(() => {
  const data = giftStore.giftModal.giftData
  const savedOccasion = data[props.step.id]
  selectedOccasion.value = savedOccasion || null
  
  // Загружаем сохраненные subOptions для выбранного случая
  if (selectedOccasion.value) {
    const savedValue = data[`${props.step.id}_${selectedOccasion.value}`]
    if (savedValue) {
      selectedSubOptions.value[selectedOccasion.value] = savedValue
    }
  }
})
</script>

<style scoped>
.occasion-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.occasion-content {
  max-width: 500px;
  width: 100%;
}

.title-section {
  margin-bottom: 32px;
  text-align: left;
}

.step-title {
  font-family: 'Gilroy';
  font-size: 32px;
  line-height: 1.09;
  font-weight: 700;
  letter-spacing: -0.64px;
  color: var(--primary-blue-100);
  margin: 0 0 12px 0;
}

.step-subtitle {
  font-family: 'Inter';
  font-size: 15px;
  color: var(--primary-blue-80);
  line-height: 1.3;
  letter-spacing: -0.64px;
  margin: 0;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 14px;
  font-weight: 500;
  color: #1d1d1f;
}

.field-select {
  width: 100%;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
}

.radio-item {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  background: #FFFFFF;
  border: 1px solid #E6E3EC;
  border-radius: 8px;
  gap: 0;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  align-self: stretch;
}

.radio-label {
  font-size: 16px;
  color: #1d1d1f;
  cursor: pointer;
}

.sub-option-select {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 20px 20px 20px;
  width: 100%;
}

@media (min-width: 1024px) {
  .occasion-step {
    padding: 0;
  }

  .occasion-content {
    padding: 40px 60px;
    gap: 40px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: center;
    align-items: center;
  }

  .step-title {
    font-family: 'Gilroy', sans-serif;
    font-size: 48px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.04em;
    color: var(--primary-blue-100);
    text-align: center;
    margin: 0;
  }

  .step-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 20px;
    font-weight: 400;
    line-height: 1.3;
    letter-spacing: -0.04em;
    color: var(--primary-blue-80);
  }

  .form-fields {
    width: 100%;
    gap: 0;
  }

  .field-group {
    width: 100%;
  }

  .radio-group {
    gap: 8px;
    width: 100%;
  }

  .radio-item {
    width: 100%;
  }

  .radio-option {
    padding: 12px 20px;
    gap: 8px;
    align-self: stretch;
  }

  .radio-label {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.3;
    letter-spacing: -0.04em;
    color: #232F45;
  }

  .sub-option-select {
    padding: 0 20px 20px 20px;
    gap: 4px;
  }
}

@media (min-width: 1919px) {
  .occasion-content {
    padding: 40px 120px;
  }
}
</style>

